import React, { createContext, useContext, useReducer, useEffect } from 'react';
import adminAuthService from '../services/adminAuth';
import toast from 'react-hot-toast';

// Initial state
const initialState = {
  admin: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  loginAttempts: 3,
  isLocked: false,
  lockedUntil: null,
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_ADMIN: 'SET_ADMIN',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOCK_STATUS: 'SET_LOCK_STATUS',
  UPDATE_ATTEMPTS: 'UPDATE_ATTEMPTS',
};

// Reducer function
function adminAuthReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ActionTypes.LOGIN_SUCCESS:
      return {
        ...state,
        admin: action.payload.admin,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        loginAttempts: 3,
        isLocked: false,
        lockedUntil: null,
      };

    case ActionTypes.LOGIN_FAILURE:
      return {
        ...state,
        admin: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload.error,
        loginAttempts: action.payload.attemptsLeft || 0,
        isLocked: action.payload.isLocked || false,
        lockedUntil: action.payload.lockedUntil || null,
      };

    case ActionTypes.LOGOUT:
      return {
        ...state,
        admin: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        loginAttempts: 3,
        isLocked: false,
        lockedUntil: null,
      };

    case ActionTypes.SET_ADMIN:
      return {
        ...state,
        admin: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null,
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case ActionTypes.SET_LOCK_STATUS:
      return {
        ...state,
        isLocked: action.payload.isLocked,
        lockedUntil: action.payload.lockedUntil,
        loginAttempts: action.payload.attemptsLeft || 0,
      };

    case ActionTypes.UPDATE_ATTEMPTS:
      return {
        ...state,
        loginAttempts: action.payload,
      };

    default:
      return state;
  }
}

// Create context
const AdminAuthContext = createContext();

// Provider component
export function AdminAuthProvider({ children }) {
  const [state, dispatch] = useReducer(adminAuthReducer, initialState);

  // Initialize authentication state on app load
  useEffect(() => {
    initializeAuth();
  }, []);

  // Initialize authentication
  const initializeAuth = async () => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    try {
      // Check if admin is stored in session
      const storedAdmin = adminAuthService.getStoredAdmin();
      const isAuthenticated = adminAuthService.isAuthenticated();

      if (isAuthenticated && storedAdmin) {
        // Try to get fresh admin data
        try {
          const currentAdmin = await adminAuthService.getCurrentAdmin();
          dispatch({
            type: ActionTypes.SET_ADMIN,
            payload: currentAdmin,
          });
        } catch (error) {
          // If getting current admin fails, use stored data
          dispatch({
            type: ActionTypes.SET_ADMIN,
            payload: storedAdmin,
          });
        }
      } else {
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  };

  // Login function
  const login = async (email, password) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    dispatch({ type: ActionTypes.CLEAR_ERROR });

    try {
      const result = await adminAuthService.login(email, password);
      
      //console.log('🎯 Dispatching LOGIN_SUCCESS with payload:', result);
      
      dispatch({
        type: ActionTypes.LOGIN_SUCCESS,
        payload: result,
      });

      //console.log('🔄 State should be updated, isAuthenticated should be true');
      toast.success(`Welcome back, ${result.admin.username}!`);
      return result;
    } catch (error) {
      const errorPayload = {
        error: error.message,
        attemptsLeft: error.attemptsLeft,
        isLocked: error.isLocked,
        lockedUntil: error.lockedUntil,
      };

      dispatch({
        type: ActionTypes.LOGIN_FAILURE,
        payload: errorPayload,
      });

      // Show appropriate error message
      if (error.isLocked) {
        toast.error('Account temporarily locked due to multiple failed attempts');
      } else if (error.attemptsLeft !== undefined) {
        toast.error(`${error.message}. ${error.attemptsLeft} attempts remaining`);
      } else {
        toast.error(error.message);
      }

      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    try {
      await adminAuthService.logout();
      dispatch({ type: ActionTypes.LOGOUT });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still dispatch logout to clear local state
      dispatch({ type: ActionTypes.LOGOUT });
      toast.error('Logout failed, but you have been signed out locally');
    }
  };

  // Check login status for email
  const checkLoginStatus = async (email) => {
    try {
      const status = await adminAuthService.checkLoginStatus(email);
      
      dispatch({
        type: ActionTypes.SET_LOCK_STATUS,
        payload: {
          isLocked: status.isLocked,
          lockedUntil: status.rateLimitInfo?.lockedUntil,
          attemptsLeft: status.rateLimitInfo?.attemptsRemaining,
        },
      });

      return status;
    } catch (error) {
      console.error('Check login status error:', error);
      throw error;
    }
  };

  // Refresh admin data
  const refreshAdmin = async () => {
    try {
      const admin = await adminAuthService.getCurrentAdmin();
      dispatch({
        type: ActionTypes.SET_ADMIN,
        payload: admin,
      });
      return admin;
    } catch (error) {
      console.error('Refresh admin error:', error);
      throw error;
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  // Check if admin has specific role
  const hasRole = (role) => {
    if (!state.admin) return false;
    if (role === 'SUPERADMIN') {
      return state.admin.role === 'SUPERADMIN';
    }
    if (role === 'ADMIN') {
      return ['ADMIN', 'SUPERADMIN'].includes(state.admin.role);
    }
    return false;
  };

  // Check if admin is super admin
  const isSuperAdmin = () => {
    return state.admin?.role === 'SUPERADMIN';
  };

  // Context value
  const value = {
    // State
    admin: state.admin,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    loginAttempts: state.loginAttempts,
    isLocked: state.isLocked,
    lockedUntil: state.lockedUntil,

    // Actions
    login,
    logout,
    checkLoginStatus,
    refreshAdmin,
    clearError,
    hasRole,
    isSuperAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// Custom hook to use admin auth context
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

export default AdminAuthContext;
